import type {
  ComponentPropsWithoutRef,
  FunctionComponent,
  MouseEventHandler,
} from 'react'
import React, { useState } from 'react'

import classnames from 'classnames'

import { Hr } from '../Hr'
import { ArrowDownIcon } from '../Icons/Navigational'
import { ChevronDownIcon } from '../Icons/Navigational/ChevronDown'
import { ChevronUpIcon } from '../Icons/Navigational/ChevronUp'
import { LinkButton } from '../LinkButton'
import { NavigationLink } from '../Links/NavigationLink'
import { TypographyV2 } from '../Seatbelt2/Foundations/Typography'
import styles from './Jumplinks.module.scss'

export const Jumplinks: FunctionComponent<Props> = ({
  items,
  className,
  title = 'In this article',
  ...rest
}) => {
  const [isExpanded, setIsExpanded] = useState(false)

  if (!items.length) {
    return null
  }

  const maxSize = 8
  const linksLength = items?.length
  const isExcessive = linksLength > maxSize
  const rowLength = !isExcessive || isExpanded ? Math.ceil(linksLength / 2) : 4

  return (
    <section
      className={classnames(styles.container, className)}
      data-testid="jumplinks"
      {...rest}
    >
      <Hr className={styles.hr} fullWidth={true} />

      <TypographyV2 textStyle="sb-text-interface-heading-small">
        {title}
      </TypographyV2>

      <ul
        className={styles.links}
        style={{ gridTemplateRows: `repeat(${rowLength}, auto)` }}
        aria-atomic
        aria-live="polite"
      >
        {items
          ?.slice(0, isExpanded ? Infinity : maxSize)
          .map(({ text, path, onClick }, index) => (
            <li key={`link_${text}_${index}`} className={styles.link}>
              <NavigationLink
                href={path}
                clickHandler={onClick}
                icon={ArrowDownIcon}
                data-testid="jumplinks-link"
                data-which-id="article-anchor-link"
                data-index={index + 1}
              >
                {text}
              </NavigationLink>
            </li>
          ))}
      </ul>

      {isExcessive && (
        <div className={styles.all}>
          <LinkButton
            onClick={() => setIsExpanded(!isExpanded)}
            icon={isExpanded ? ChevronUpIcon : ChevronDownIcon}
            aria-atomic
            aria-pressed={isExpanded}
            data-testid="jumplinks-toggle-link"
          >
            {isExpanded ? 'View fewer links' : 'View more links'}
          </LinkButton>
        </div>
      )}

      <Hr className={styles.hr} fullWidth={true} />
    </section>
  )
}

///////// IMPLEMENTATION /////////

type Props = {
  /**
   * An array of links with the text and path supplied as string values, with an optional onClick for
   * implementing smooth scroll behaviour.
   */
  items: Array<{
    text: string
    path: string
    onClick?: MouseEventHandler<HTMLAnchorElement>
  }>
  className?: string
  /**
   * Optional title displayed at the top of the component, default is set to 'In this article'
   */
  title?: string
} & ComponentPropsWithoutRef<'section'>
